// alt.js — alt-text composer (plan Step 6)
//
// Every slice MUST have non-empty alt text (guardrail 14; altifier's
// Section 508 posture). A 10-slice panorama is opaque to screen-reader
// users without per-slice alt. Default template:
//   "{n} of {total} — {userDescription}"
// with a shared description plus optional per-slice overrides. The
// description is dropped (no dangling dash) when empty, so the alt stays
// clean but position-aware.

export const DEFAULT_ALT_TEMPLATE = (n, total, description) =>
  description ? `${n} of ${total} — ${description}` : `${n} of ${total}`;

/**
 * Build alt text for all N slices.
 *
 * @param {number} total slice count
 * @param {string} [sharedDescription=''] description applied to every slice
 * @param {Object<string,string>} [overrides={}] sliceIndex (1-based) ->
 *   replacement alt; any non-empty override wins over the template.
 * @returns {string[]} length `total`, index 0 = slice 1
 */
export function buildAlts(total, sharedDescription = '', overrides = {}) {
  const alts = [];

  for (let i = 1; i <= total; i++) {
    const override = overrides[i];

    // Boundary parse: override is a string or undefined.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    if (typeof override === 'string' && override.trim() !== '') {
      alts.push(override.trim());
    } else {
      alts.push(DEFAULT_ALT_TEMPLATE(i, total, (sharedDescription || '').trim()));
    }
  }

  return alts;
}

/**
 * True when every slice has non-empty (after trim) alt text. Publish must
 * be gated on this (plan Step 6 acceptance).
 */
export function allAltsValid(alts) {
  return (
    Array.isArray(alts) &&
    alts.length > 0 &&
    // Boundary parse: alts entries are strings.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    alts.every((a) => typeof a === 'string' && a.trim() !== '')
  );
}